/**
 * Message Cache Service using IndexedDB + In-Memory Cache
 * 
 * Provides instant message loading by caching the last 50 messages per conversation.
 * 
 * Features:
 * - In-memory cache for recently viewed conversations (truly instant, synchronous)
 * - IndexedDB for persistent storage (fast async)
 * - Background sync with Firestore
 * - Offline support (read-only)
 * - Automatic cache cleanup for old conversations
 */

const DB_NAME = 'MessagingCache';
const DB_VERSION = 2;
const MESSAGES_STORE = 'messages';
const CONVERSATIONS_STORE = 'conversations';
const CONVERSATION_LIST_STORE = 'conversationList';
const MAX_MESSAGES_PER_CONVERSATION = 50;
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// In-memory cache for recently viewed conversations (max 10)
const MAX_MEMORY_CACHE_SIZE = 10;
const memoryCache = new Map();

let dbInstance = null;
let dbPromise = null;

/**
 * Get messages from in-memory cache (synchronous, instant)
 * Returns null if not in memory cache
 */
export function getMemoryCachedMessages(conversationId) {
  return memoryCache.get(conversationId) || null;
}

/**
 * Store messages in memory cache
 */
export function setMemoryCachedMessages(conversationId, messages) {
  // Evict oldest if at capacity
  if (memoryCache.size >= MAX_MEMORY_CACHE_SIZE && !memoryCache.has(conversationId)) {
    const oldestKey = memoryCache.keys().next().value;
    memoryCache.delete(oldestKey);
  }
  memoryCache.set(conversationId, messages);
}

/**
 * Clear memory cache for a conversation
 */
export function clearMemoryCache(conversationId) {
  if (conversationId) {
    memoryCache.delete(conversationId);
  } else {
    memoryCache.clear();
  }
}

/**
 * Initialize and get the IndexedDB database
 */
function getDB() {
  if (dbInstance) return Promise.resolve(dbInstance);
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('[MessageCache] Failed to open IndexedDB:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Messages store - indexed by conversationId for fast lookups
      if (!db.objectStoreNames.contains(MESSAGES_STORE)) {
        const messagesStore = db.createObjectStore(MESSAGES_STORE, { keyPath: 'id' });
        messagesStore.createIndex('conversationId', 'conversationId', { unique: false });
        messagesStore.createIndex('conversationId_createdAt', ['conversationId', 'createdAtMs'], { unique: false });
      }

      // Conversations metadata store (for message caching metadata)
      if (!db.objectStoreNames.contains(CONVERSATIONS_STORE)) {
        db.createObjectStore(CONVERSATIONS_STORE, { keyPath: 'id' });
      }

      // Conversation list store (for caching the full conversation list per user)
      if (!db.objectStoreNames.contains(CONVERSATION_LIST_STORE)) {
        const convListStore = db.createObjectStore(CONVERSATION_LIST_STORE, { keyPath: 'id' });
        convListStore.createIndex('userId', 'userId', { unique: false });
      }
    };
  });

  return dbPromise;
}

/**
 * Convert Firestore timestamp to milliseconds for indexing
 */
function getTimestampMs(timestamp) {
  if (!timestamp) return Date.now();
  if (timestamp.toDate) return timestamp.toDate().getTime();
  if (timestamp instanceof Date) return timestamp.getTime();
  if (typeof timestamp === 'number') return timestamp;
  return new Date(timestamp).getTime();
}

/**
 * Convert Firestore timestamp to ISO string for storage
 */
function getTimestampISO(timestamp) {
  if (!timestamp) return null;
  if (timestamp.toDate) return timestamp.toDate().toISOString();
  if (timestamp instanceof Date) return timestamp.toISOString();
  if (typeof timestamp === 'string') return timestamp;
  if (typeof timestamp === 'number') return new Date(timestamp).toISOString();
  return null;
}

/**
 * Sanitize a conversation object for IndexedDB storage
 * Only picks serializable fields - avoids spreading Firebase internals
 */
function sanitizeConversationForCache(conv) {
  // Explicitly pick only the fields we need - don't spread the whole object
  // This avoids copying Firebase internal references like PlatformLoggerServiceImpl
  const sanitized = {
    id: conv.id,
    type: conv.type,
    name: conv.name,
    participants: conv.participants ? [...conv.participants] : [],
    pinnedBy: conv.pinnedBy ? [...conv.pinnedBy] : [],
    createdBy: conv.createdBy,
    createdAt: getTimestampISO(conv.createdAt),
    updatedAt: getTimestampISO(conv.updatedAt),
  };

  // Deep clone participantDetails to avoid Firebase references
  if (conv.participantDetails) {
    sanitized.participantDetails = {};
    for (const [key, value] of Object.entries(conv.participantDetails)) {
      sanitized.participantDetails[key] = {
        name: value?.name || null,
        avatar: value?.avatar || null
      };
    }
  }

  // Sanitize lastMessage - only pick needed fields
  if (conv.lastMessage) {
    sanitized.lastMessage = {
      text: conv.lastMessage.text || null,
      senderId: conv.lastMessage.senderId || null,
      senderName: conv.lastMessage.senderName || null,
      type: conv.lastMessage.type || null,
      timestamp: getTimestampISO(conv.lastMessage.timestamp)
    };
  }

  // Copy other simple fields if they exist
  if (conv.pinnedAt) {
    sanitized.pinnedAt = typeof conv.pinnedAt === 'object'
      ? JSON.parse(JSON.stringify(conv.pinnedAt))
      : conv.pinnedAt;
  }
  if (conv.manuallyMarkedUnread) {
    sanitized.manuallyMarkedUnread = typeof conv.manuallyMarkedUnread === 'object'
      ? JSON.parse(JSON.stringify(conv.manuallyMarkedUnread))
      : conv.manuallyMarkedUnread;
  }
  if (conv.removedParticipants) {
    sanitized.removedParticipants = typeof conv.removedParticipants === 'object'
      ? JSON.parse(JSON.stringify(conv.removedParticipants))
      : conv.removedParticipants;
  }

  return sanitized;
}

/**
 * Sanitize a message object for IndexedDB storage
 * Only picks serializable fields - avoids spreading Firebase internals
 */
function sanitizeMessageForCache(msg, conversationId) {
  // Explicitly pick only the fields we need
  const sanitized = {
    id: msg.id,
    conversationId,
    senderId: msg.senderId || null,
    senderName: msg.senderName || null,
    text: msg.text || null,
    type: msg.type || 'text',
    createdAtMs: getTimestampMs(msg.createdAt),
    createdAt: msg.createdAt?.toDate?.()
      ? msg.createdAt.toDate().toISOString()
      : (typeof msg.createdAt === 'string' ? msg.createdAt : null),
  };

  // Copy optional fields if they exist
  if (msg.imageUrl) sanitized.imageUrl = msg.imageUrl;
  if (msg.voiceUrl) sanitized.voiceUrl = msg.voiceUrl;
  if (msg.duration) sanitized.duration = msg.duration;
  if (msg.subtype) sanitized.subtype = msg.subtype;
  if (msg.replyTo) {
    sanitized.replyTo = {
      id: msg.replyTo.id || null,
      text: msg.replyTo.text || null,
      senderName: msg.replyTo.senderName || null
    };
  }
  if (msg.reactions) {
    // Deep clone reactions to avoid any Firebase references
    sanitized.reactions = JSON.parse(JSON.stringify(msg.reactions));
  }
  if (msg.readBy) {
    sanitized.readBy = Array.isArray(msg.readBy) ? [...msg.readBy] : [];
  }

  return sanitized;
}

/**
 * Get cached messages for a conversation
 * @returns {Promise<Array>} Cached messages sorted by createdAt
 */
export async function getCachedMessages(conversationId) {
  try {
    const db = await getDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(MESSAGES_STORE, 'readonly');
      const store = transaction.objectStore(MESSAGES_STORE);
      const index = store.index('conversationId');
      const request = index.getAll(conversationId);

      request.onsuccess = () => {
        const messages = request.result || [];
        // Sort by createdAt ascending (oldest first)
        messages.sort((a, b) => (a.createdAtMs || 0) - (b.createdAtMs || 0));
        resolve(messages);
      };

      request.onerror = () => {
        console.error('[MessageCache] Error reading cache:', request.error);
        resolve([]); // Return empty on error, don't block
      };
    });
  } catch (err) {
    console.error('[MessageCache] getCachedMessages error:', err);
    return [];
  }
}

/**
 * Save messages to cache for a conversation
 * Keeps only the latest MAX_MESSAGES_PER_CONVERSATION messages
 */
export async function cacheMessages(conversationId, messages) {
  if (!messages || messages.length === 0) return;

  try {
    const db = await getDB();

    // Prepare messages for storage - use sanitization to avoid Firebase internals
    const messagesToCache = messages
      .map(msg => sanitizeMessageForCache(msg, conversationId))
      .sort((a, b) => b.createdAtMs - a.createdAtMs) // Sort newest first
      .slice(0, MAX_MESSAGES_PER_CONVERSATION); // Keep only latest 50

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([MESSAGES_STORE, CONVERSATIONS_STORE], 'readwrite');
      const messagesStore = transaction.objectStore(MESSAGES_STORE);
      const conversationsStore = transaction.objectStore(CONVERSATIONS_STORE);

      // First, delete old messages for this conversation
      const index = messagesStore.index('conversationId');
      const deleteRequest = index.openCursor(conversationId);
      
      deleteRequest.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };

      transaction.oncomplete = () => {
        // Now add the new messages in a new transaction
        const addTransaction = db.transaction([MESSAGES_STORE, CONVERSATIONS_STORE], 'readwrite');
        const addStore = addTransaction.objectStore(MESSAGES_STORE);
        const convStore = addTransaction.objectStore(CONVERSATIONS_STORE);

        messagesToCache.forEach(msg => {
          addStore.put(msg);
        });

        // Update conversation metadata
        convStore.put({
          id: conversationId,
          cachedAt: Date.now(),
          messageCount: messagesToCache.length
        });

        addTransaction.oncomplete = () => resolve();
        addTransaction.onerror = () => {
          console.error('[MessageCache] Error saving to cache:', addTransaction.error);
          resolve(); // Don't block on cache errors
        };
      };

      transaction.onerror = () => {
        console.error('[MessageCache] Error clearing old cache:', transaction.error);
        resolve();
      };
    });
  } catch (err) {
    console.error('[MessageCache] cacheMessages error:', err);
  }
}

/**
 * Add or update a single message in the cache
 */
export async function cacheMessage(conversationId, message) {
  try {
    const db = await getDB();

    // Use sanitization to avoid Firebase internals
    const messageToCache = sanitizeMessageForCache(message, conversationId);

    return new Promise((resolve) => {
      const transaction = db.transaction(MESSAGES_STORE, 'readwrite');
      const store = transaction.objectStore(MESSAGES_STORE);
      store.put(messageToCache);
      
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => {
        console.error('[MessageCache] Error caching message:', transaction.error);
        resolve();
      };
    });
  } catch (err) {
    console.error('[MessageCache] cacheMessage error:', err);
  }
}

/**
 * Remove a message from cache (for deletions)
 */
export async function removeCachedMessage(messageId) {
  try {
    const db = await getDB();
    
    return new Promise((resolve) => {
      const transaction = db.transaction(MESSAGES_STORE, 'readwrite');
      const store = transaction.objectStore(MESSAGES_STORE);
      store.delete(messageId);
      
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => resolve();
    });
  } catch (err) {
    console.error('[MessageCache] removeCachedMessage error:', err);
  }
}

/**
 * Clear cache for a specific conversation
 */
export async function clearConversationCache(conversationId) {
  try {
    const db = await getDB();
    
    return new Promise((resolve) => {
      const transaction = db.transaction([MESSAGES_STORE, CONVERSATIONS_STORE], 'readwrite');
      const messagesStore = transaction.objectStore(MESSAGES_STORE);
      const conversationsStore = transaction.objectStore(CONVERSATIONS_STORE);

      // Delete all messages for this conversation
      const index = messagesStore.index('conversationId');
      const request = index.openCursor(conversationId);
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };

      // Delete conversation metadata
      conversationsStore.delete(conversationId);

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => resolve();
    });
  } catch (err) {
    console.error('[MessageCache] clearConversationCache error:', err);
  }
}

/**
 * Clean up old cached conversations (older than CACHE_TTL_MS)
 */
export async function cleanupOldCache() {
  try {
    const db = await getDB();
    const cutoffTime = Date.now() - CACHE_TTL_MS;
    
    return new Promise((resolve) => {
      const transaction = db.transaction([MESSAGES_STORE, CONVERSATIONS_STORE], 'readwrite');
      const conversationsStore = transaction.objectStore(CONVERSATIONS_STORE);
      const messagesStore = transaction.objectStore(MESSAGES_STORE);

      const request = conversationsStore.openCursor();
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const conv = cursor.value;
          if (conv.cachedAt < cutoffTime) {
            // Delete old conversation and its messages
            cursor.delete();
            
            const msgIndex = messagesStore.index('conversationId');
            const msgRequest = msgIndex.openCursor(conv.id);
            msgRequest.onsuccess = (e) => {
              const msgCursor = e.target.result;
              if (msgCursor) {
                msgCursor.delete();
                msgCursor.continue();
              }
            };
          }
          cursor.continue();
        }
      };

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => resolve();
    });
  } catch (err) {
    console.error('[MessageCache] cleanupOldCache error:', err);
  }
}

/**
 * Check if we have cached messages for a conversation
 */
export async function hasCachedMessages(conversationId) {
  try {
    const db = await getDB();
    
    return new Promise((resolve) => {
      const transaction = db.transaction(CONVERSATIONS_STORE, 'readonly');
      const store = transaction.objectStore(CONVERSATIONS_STORE);
      const request = store.get(conversationId);

      request.onsuccess = () => {
        resolve(!!request.result);
      };

      request.onerror = () => resolve(false);
    });
  } catch (err) {
    return false;
  }
}

// ==================== CONVERSATION LIST CACHING ====================

/**
 * Get cached conversation list for a user
 */
export async function getCachedConversationList(userId) {
  try {
    const db = await getDB();
    
    return new Promise((resolve) => {
      const transaction = db.transaction(CONVERSATION_LIST_STORE, 'readonly');
      const store = transaction.objectStore(CONVERSATION_LIST_STORE);
      const index = store.index('userId');
      const request = index.getAll(userId);

      request.onsuccess = () => {
        const conversations = request.result || [];
        // Sort by updatedAt descending (most recent first)
        conversations.sort((a, b) => (b.updatedAtMs || 0) - (a.updatedAtMs || 0));
        resolve(conversations);
      };

      request.onerror = () => {
        console.error('[MessageCache] Error reading conversation list cache:', request.error);
        resolve([]);
      };
    });
  } catch (err) {
    console.error('[MessageCache] getCachedConversationList error:', err);
    return [];
  }
}

/**
 * Cache conversation list for a user
 */
export async function cacheConversationList(userId, conversations) {
  if (!conversations || conversations.length === 0) return;

  try {
    const db = await getDB();

    // Prepare conversations for storage - sanitize to remove non-serializable data
    const toCache = conversations.map(conv => {
      const sanitized = sanitizeConversationForCache(conv);
      return {
        ...sanitized,
        id: `${userId}_${conv.id}`, // Composite key for uniqueness per user
        originalId: conv.id, // Keep original id for reference
        userId,
        updatedAtMs: getTimestampMs(conv.updatedAt),
        cachedAt: Date.now()
      };
    });

    return new Promise((resolve) => {
      const transaction = db.transaction(CONVERSATION_LIST_STORE, 'readwrite');
      const store = transaction.objectStore(CONVERSATION_LIST_STORE);

      // First clear old conversations for this user
      const index = store.index('userId');
      const clearRequest = index.openCursor(userId);
      
      clearRequest.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };

      transaction.oncomplete = () => {
        // Add new conversations in a new transaction
        const addTransaction = db.transaction(CONVERSATION_LIST_STORE, 'readwrite');
        const addStore = addTransaction.objectStore(CONVERSATION_LIST_STORE);

        toCache.forEach(conv => {
          addStore.put(conv);
        });

        addTransaction.oncomplete = () => resolve();
        addTransaction.onerror = () => {
          console.error('[MessageCache] Error caching conversation list:', addTransaction.error);
          resolve();
        };
      };

      transaction.onerror = () => resolve();
    });
  } catch (err) {
    console.error('[MessageCache] cacheConversationList error:', err);
  }
}

/**
 * Update a single conversation in the cache
 */
export async function updateCachedConversation(userId, conversation) {
  try {
    const db = await getDB();

    // Sanitize to remove non-serializable Firestore data
    const sanitized = sanitizeConversationForCache(conversation);
    const toCache = {
      ...sanitized,
      id: `${userId}_${conversation.id}`,
      originalId: conversation.id,
      userId,
      updatedAtMs: getTimestampMs(conversation.updatedAt),
      cachedAt: Date.now()
    };

    return new Promise((resolve) => {
      const transaction = db.transaction(CONVERSATION_LIST_STORE, 'readwrite');
      const store = transaction.objectStore(CONVERSATION_LIST_STORE);
      store.put(toCache);
      
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => resolve();
    });
  } catch (err) {
    console.error('[MessageCache] updateCachedConversation error:', err);
  }
}

// Run cleanup on module load (non-blocking)
if (typeof window !== 'undefined') {
  setTimeout(() => {
    cleanupOldCache().catch(() => {});
  }, 5000);
}
